import { Button, Col, Divider, Radio, Row, Select, Tag } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import TrademarkContext from "../../../context/trademark/context";
import TrademarkContextProvider from "../../../context/trademark/provider";
import { MediumFor } from "../../../models/medium/enum";
import { IUpdateTrademark } from "../../../models/trademark/request";
import { languages } from "../../../utils/helpers/constants";
import { getTranslations } from "../../../utils/helpers/translations";
import Controller from "../../form-components/controller";
import FieldBuilder from "../../form-components/field-builder";
import ImageUploader from "../../form-components/image-uploader-client";
import Spin from "../../general/antd/spin";
import FormItem from "../../general/form-item";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { ITag } from "../../../models/tag/response";
import styles from "./style.module.scss";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";
import Image from "../../general/antd/image";
import { errorNotification, successNotification } from "../../../utils/helpers/notification";
import { appendImageProcessingFields, getImageProcessingMode, hasPendingImageUpload, ImageProcessingMode, resolveUploadFile } from "../../../utils/helpers/media-upload";
import { DeleteOutlined } from "@ant-design/icons";

const Form = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(TrademarkContext);
    const { id } = useParams();
    const [tags, setTags] = useState<ITag[]>([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [tagKeyword, setTagKeyword] = useState<string>();
    const navigate = useNavigate();
    const { direction } = useContext(AppContext);

    const [newImage, setNewImage] = useState<string | null>(null);
    const [newImageFile, setNewImageFile] = useState<File | undefined>();
    const [originalImageFile, setOriginalImageFile] = useState<File | undefined>();
    const [imageChanged, setImageChanged] = useState(false);
    const [imageLoading, setImageLoading] = useState<boolean>(false);
    const [preserveOriginalImage, setPreserveOriginalImage] = useState(false);
    const [currentImageHidden, setCurrentImageHidden] = useState(false);

    const [newLogo, setNewLogo] = useState<string | null>(null);
    const [newLogoFile, setNewLogoFile] = useState<File | undefined>();
    const [originalLogoFile, setOriginalLogoFile] = useState<File | undefined>();
    const [logoChanged, setLogoChanged] = useState(false);
    const [logoLoading, setLogoLoading] = useState<boolean>(false);
    const [preserveOriginalLogo, setPreserveOriginalLogo] = useState(false);
    const [currentLogoHidden, setCurrentLogoHidden] = useState(false);

    const handleUploadNewImage = async (isLogo) => {
        const imageUrl = isLogo ? newLogo : newImage;

        if (!imageUrl) {
            errorNotification(t("no_new_image_to_upload"));
            return;
        }
        isLogo ? setLogoLoading(true) : setImageLoading(true)

        const formData = new FormData();
        try {
            const preserveOriginal = isLogo ? preserveOriginalLogo : preserveOriginalImage;
            const file = await resolveUploadFile({
                imageUrl,
                processedFile: isLogo ? newLogoFile : newImageFile,
                originalFile: isLogo ? originalLogoFile : originalImageFile,
                preserveOriginal,
                fallbackName: isLogo ? "logo" : "image",
            });

            formData.append("medium", file);
            formData.append("type", "1");
            formData.append("for", MediumFor.UserProfile);
            appendImageProcessingFields(
                formData,
                getImageProcessingMode(Boolean(isLogo ? withoutBackgroundLogo : withoutBackground), preserveOriginal),
            );

            const uploadResponse = await EndPoints.media.uploadMedia(formData);
            if (isLogo) {
                setValue("logoId", uploadResponse.id, { shouldDirty: true });
                setNewLogo(uploadResponse?.url ?? imageUrl);
                setNewLogoFile(undefined);
                setOriginalLogoFile(undefined);
                setPreserveOriginalLogo(false);
                setLogoChanged(false);
            } else {
                setValue("imageId", uploadResponse.id, { shouldDirty: true });
                setNewImage(uploadResponse?.url ?? imageUrl);
                setNewImageFile(undefined);
                setOriginalImageFile(undefined);
                setPreserveOriginalImage(false);
                setImageChanged(false);
            }

            successNotification(t("image_uploaded_successfully"));
        } catch (error) {
            errorNotification(t("failed_to_upload_image"));
        } finally {
            isLogo ? setLogoLoading(false) : setImageLoading(false)
        }
    };

    const Option = Select.Option;
    const methods = useForm<any>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            imageId: details?.image?.id ?? undefined,
            logoId: details?.logo?.id ?? undefined,
            isNew: details?.isNew ?? false,
            exportPurchaseInvoiceReport: details?.exportPurchaseInvoiceReport ?? false,
            hasOffers: details?.hasOffers ?? false,
            isSpecial: details?.isNew ?? false,
            tagIds: details?.tags?.map((tag) => tag?.id),
            order: details?.order,
            minOrderValueForFreeDelivery: details?.minOrderValueForFreeDelivery,
            maxOrderValueForFreeDelivery: details?.maxOrderValueForFreeDelivery,
            acceptedExchange: details?.acceptedExchange ?? false,
            without_background: details?.image?.without_background ?? false,
            without_background_logo: details?.logo?.without_background ?? false,

        } as IUpdateTrademark,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        trigger,
    } = methods;

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            imageId: details?.image?.id ?? undefined,
            logoId: details?.logo?.id ?? undefined,
            isNew: details?.isNew ?? false,
            exportPurchaseInvoiceReport: details?.exportPurchaseInvoiceReport ?? false,
            hasOffers: details?.hasOffers ?? false,
            isSpecial: details?.isNew ?? false,
            tagIds: details?.tags?.map((tag) => tag?.id),
            order: details?.order,
            isOfferingFreeDelivery: details?.isOfferingFreeDelivery,
            minOrderValueForFreeDelivery: details?.minOrderValueForFreeDelivery,
            maxOrderValueForFreeDelivery: details?.maxOrderValueForFreeDelivery,
            acceptedExchange: details?.acceptedExchange ?? false,
            without_background: details?.image?.without_background ?? false,
            without_background_logo: details?.logo?.without_background ?? false,
        } as IUpdateTrademark);
    }, [details]);

    useEffect(() => {
        setCurrentImageHidden(false);
    }, [details?.image?.id]);

    useEffect(() => {
        setCurrentLogoHidden(false);
    }, [details?.logo?.id]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    useEffect(() => {
        const getTags = async () => {
            try {
                const { data } = await EndPoints.tag.getAllTags({
                    light: true,
                    perPage: -1,
                });
                setTags(data.data);
            } catch (error) {
            } finally {
                setTagsLoading(false);
            }
        };
        getTags();
    }, [direction]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        if (
            hasPendingImageUpload(imageChanged, newImage) ||
            hasPendingImageUpload(logoChanged, newLogo)
        ) {
            errorNotification(t("pending_image_upload_message"));
            return;
        }

        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateTrademark(details?.id, data)
                    : await actions.createTrademark(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    const currentImageId = useWatch({ control, name: "imageId" });

    useEffect(() => {
        if (newImageFile && details?.image?.id !== currentImageId) {
            setImageChanged(true);
        } else {
            setImageChanged(false);
        }
    }, [currentImageId, details?.image?.id, newImageFile]);

    const currentLogoId = useWatch({ control, name: "logoId" });

    useEffect(() => {
        if (newLogoFile && details?.logo?.id !== currentLogoId) {
            setLogoChanged(true);
        } else {
            setLogoChanged(false);
        }
    }, [currentLogoId, details?.logo?.id, newLogoFile]);

    const withoutBackground = useWatch({ control, name: "without_background" });
    const withoutBackgroundLogo = useWatch({ control, name: "without_background_logo" });
    const imageProcessingMode = getImageProcessingMode(Boolean(withoutBackground), preserveOriginalImage);
    const logoProcessingMode = getImageProcessingMode(Boolean(withoutBackgroundLogo), preserveOriginalLogo);
    const currentImageVisible = details?.image && !currentImageHidden;
    const currentLogoVisible = details?.logo && !currentLogoHidden;
    const imageDeleteButtonStyle: React.CSSProperties = {
        position: "absolute",
        top: 8,
        zIndex: 2,
        background: "rgba(255,255,255,.92)",
        boxShadow: "0 2px 8px rgba(0,0,0,.12)",
        ...(direction === "rtl" ? { left: 8 } : { right: 8 }),
    };

    const handleRemoveNewImage = async () => {
        const shouldRestoreCurrentImage = Boolean(details?.image && !currentImageHidden);

        setValue("imageId", shouldRestoreCurrentImage ? details?.image?.id : undefined, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        setValue("without_background", shouldRestoreCurrentImage ? details?.image?.without_background ?? false : false);
        await trigger("imageId");
        setNewImage(null);
        setNewImageFile(undefined);
        setOriginalImageFile(undefined);
        setPreserveOriginalImage(false);
        setImageChanged(false);
    };

    const handleRemoveNewLogo = async () => {
        const shouldRestoreCurrentLogo = Boolean(details?.logo && !currentLogoHidden);

        setValue("logoId", shouldRestoreCurrentLogo ? details?.logo?.id : undefined, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        setValue("without_background_logo", shouldRestoreCurrentLogo ? details?.logo?.without_background ?? false : false);
        await trigger("logoId");
        setNewLogo(null);
        setNewLogoFile(undefined);
        setOriginalLogoFile(undefined);
        setPreserveOriginalLogo(false);
        setLogoChanged(false);
    };

    const handleDeleteCurrentImage = async () => {
        setValue("imageId", null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        await trigger("imageId");
        setCurrentImageHidden(true);
        setNewImage(null);
        setNewImageFile(undefined);
        setOriginalImageFile(undefined);
        setPreserveOriginalImage(false);
        setImageChanged(false);
    };

    const handleDeleteCurrentLogo = async () => {
        setValue("logoId", null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
        await trigger("logoId");
        setCurrentLogoHidden(true);
        setNewLogo(null);
        setNewLogoFile(undefined);
        setOriginalLogoFile(undefined);
        setPreserveOriginalLogo(false);
        setLogoChanged(false);
    };
    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_trademark") : t("add_trademark")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`name[${lang?.code}]`}
                                                label={`${t("name")} (${lang?.name})`}
                                                input={{ type: "text" }}
                                                rules={{ required: true }}
                                            />
                                        </Col>
                                    );
                                })}
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="order"
                                        label={t("order")}
                                        input={{ type: "number" }}
                                        rules={{ required: true }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="minOrderValueForFreeDelivery"
                                        label={t("min_order_value_for_free_delivery")}
                                        input={{ type: "number" }}
                                        rules={{ required: false }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FieldBuilder
                                        name="maxOrderValueForFreeDelivery"
                                        label={t("max_order_value_for_free_delivery")}
                                        input={{ type: "number" }}
                                        rules={{ required: false }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6}>
                                    <FormItem label={t("tags")} required>
                                        <Controller
                                            name="tagIds"
                                            control={control}
                                            rules={{ required: true }}
                                            render={({ field: { ...field } }) => {
                                                return (
                                                    <Select
                                                        loading={tagsLoading}
                                                        {...field}
                                                        onSearch={(val) => {
                                                            setTagKeyword(val);
                                                        }}
                                                        style={{ width: "100%" }}
                                                        allowClear
                                                        mode="multiple"
                                                        placeholder={t("tags")}
                                                        filterOption={false}
                                                    >
                                                        {tags
                                                            .filter((tr) =>
                                                                tagKeyword
                                                                    ? tr?.name?.includes(tagKeyword)
                                                                    : true
                                                            )
                                                            .map((tr, index) => {
                                                                return (
                                                                    <Option
                                                                        key={index}
                                                                        value={tr?.id}
                                                                        label={
                                                                            <Tag color={tr?.color}>{tr.name}</Tag>
                                                                        }
                                                                    >
                                                                        <Tag color={tr?.color}>{tr?.name}</Tag>
                                                                    </Option>
                                                                );
                                                            })}
                                                    </Select>
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col sm={0} md={12} lg={0}>
                                </Col>
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("is_new")}`}
                                        name="isNew"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("export_purchase_invoice_report")}`}
                                        name="exportPurchaseInvoiceReport"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {details && (
                                    <Col span={6}>
                                        <FieldBuilder
                                            label={`${t("free_delivery")}`}
                                            name="isOfferingFreeDelivery"
                                            input={{ type: "checkBox" }}
                                        />
                                    </Col>
                                )}
                                <Col span={6}>
                                    <FieldBuilder
                                        label={`${t("accepted_exchange")}`}
                                        name="acceptedExchange"
                                        input={{ type: "checkBox" }}
                                    />
                                </Col>
                                {details && (
                                    <Col span={6}>
                                        <FieldBuilder
                                            label={`${t("has_offers")}`}
                                            name="hasOffers"
                                            input={{ type: "checkBox" }}
                                        />
                                    </Col>
                                )}
                                {languages?.map((lang, index) => {
                                    return (
                                        <Col span={12}>
                                            <FieldBuilder
                                                key={index}
                                                name={`note[${lang?.code}]`}
                                                label={`${t("note")} (${lang?.name})`}
                                                input={{ type: "text-area" }}
                                                rules={{ required: false }}
                                            />
                                        </Col>
                                    );
                                })}
                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{
                                                required: { value: true, message: `${t("field_is_required_message")}` },
                                            }}
                                            render={({ field: { ref, ...field } }) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        imageFor={MediumFor.UserProfile}
                                                        preserveOriginal={preserveOriginalImage}
                                                        onChange={(response) => {
                                                            if (response) {
                                                                setNewImage(response.url);
                                                                setNewImageFile(response.file);
                                                                setOriginalImageFile(response.originalFile);
                                                                setValue("without_background", false);
                                                            } else {
                                                                setNewImage(null);
                                                                setNewImageFile(undefined);
                                                                setOriginalImageFile(undefined);
                                                                setValue("without_background", details?.image?.without_background ?? false);
                                                            }
                                                            setPreserveOriginalImage(false);
                                                            field.onChange(response ? response.id : currentImageHidden ? undefined : details?.image?.id);
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                                    {currentImageVisible && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("old_image")}</h4>
                                            <div style={{ position: "relative" }}>
                                                {!newImage && (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        shape="circle"
                                                        icon={<DeleteOutlined />}
                                                        aria-label={t("delete")}
                                                        title={t("delete")}
                                                        onClick={handleDeleteCurrentImage}
                                                        style={imageDeleteButtonStyle}
                                                    />
                                                )}
                                                <Image src={details.image.url} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                            </div>
                                        </div>
                                    )}

                                    {newImage && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("new_image")}</h4>
                                            <div style={{ position: "relative" }}>
                                                <Button
                                                    type="text"
                                                    danger
                                                    shape="circle"
                                                    icon={<DeleteOutlined />}
                                                    aria-label={t("delete")}
                                                    title={t("delete")}
                                                    onClick={handleRemoveNewImage}
                                                    style={imageDeleteButtonStyle}
                                                />
                                                <Image src={newImage} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {imageChanged && newImage && (
                                <Col span={24}>
                                    <FormItem label={t("image_processing_type")}>
                                        <Radio.Group
                                            value={imageProcessingMode}
                                            optionType="button"
                                            buttonStyle="solid"
                                            onChange={(e) => {
                                                const mode = e.target.value;
                                                setValue("without_background", mode === ImageProcessingMode.WithoutBackground);
                                                setPreserveOriginalImage(mode === ImageProcessingMode.Original);
                                            }}
                                        >
                                            <Radio.Button value={ImageProcessingMode.Avif}>
                                                {t("image_processing_avif")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.Original}>
                                                {t("preserve_original")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.WithoutBackground}>
                                                {t("without_background")}
                                            </Radio.Button>
                                        </Radio.Group>
                                    </FormItem>
                                </Col>
                                )}
                                {imageChanged && (
                                    <Button type="primary" onClick={() => handleUploadNewImage(false)} style={{ marginTop: 16 }} loading={imageLoading}>
                                        {t("upload_new_image")}
                                    </Button>
                                )}
                                <Col span={24}>
                                    <FormItem label={t("logo")} required>
                                        <Controller
                                            name="logoId"
                                            rules={{
                                                required: { value: true, message: `${t("field_is_required_message")}` },
                                            }}
                                            render={({ field: { ref, ...field } }) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        imageFor={MediumFor.UserProfile}
                                                        preserveOriginal={preserveOriginalLogo}
                                                        onChange={(response) => {
                                                            if (response) {
                                                                setNewLogo(response.url);
                                                                setNewLogoFile(response.file);
                                                                setOriginalLogoFile(response.originalFile);
                                                                setValue("without_background_logo", false);
                                                            } else {
                                                                setNewLogo(null);
                                                                setNewLogoFile(undefined);
                                                                setOriginalLogoFile(undefined);
                                                                setValue("without_background_logo", details?.logo?.without_background ?? false);
                                                            }
                                                            setPreserveOriginalLogo(false);
                                                            field.onChange(response ? response.id : currentLogoHidden ? undefined : details?.logo?.id);
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
                                    {currentLogoVisible && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("old_image")}</h4>
                                            <div style={{ position: "relative" }}>
                                                {!newLogo && (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        shape="circle"
                                                        icon={<DeleteOutlined />}
                                                        aria-label={t("delete")}
                                                        title={t("delete")}
                                                        onClick={handleDeleteCurrentLogo}
                                                        style={imageDeleteButtonStyle}
                                                    />
                                                )}
                                                <Image src={details.logo.url} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                            </div>
                                        </div>
                                    )}

                                    {newLogo && (
                                        <div style={{ textAlign: "center", flex: "1 1 300px" }}>
                                            <h4>{t("new_image")}</h4>
                                            <div style={{ position: "relative" }}>
                                                <Button
                                                    type="text"
                                                    danger
                                                    shape="circle"
                                                    icon={<DeleteOutlined />}
                                                    aria-label={t("delete")}
                                                    title={t("delete")}
                                                    onClick={handleRemoveNewLogo}
                                                    style={imageDeleteButtonStyle}
                                                />
                                                <Image src={newLogo} preview={true} style={{ width: "100%", maxHeight: 300 }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {logoChanged && newLogo && (
                                <Col span={24}>
                                    <FormItem label={t("image_processing_type")}>
                                        <Radio.Group
                                            value={logoProcessingMode}
                                            optionType="button"
                                            buttonStyle="solid"
                                            onChange={(e) => {
                                                const mode = e.target.value;
                                                setValue("without_background_logo", mode === ImageProcessingMode.WithoutBackground);
                                                setPreserveOriginalLogo(mode === ImageProcessingMode.Original);
                                            }}
                                        >
                                            <Radio.Button value={ImageProcessingMode.Avif}>
                                                {t("image_processing_avif")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.Original}>
                                                {t("preserve_original")}
                                            </Radio.Button>
                                            <Radio.Button value={ImageProcessingMode.WithoutBackground}>
                                                {t("without_background")}
                                            </Radio.Button>
                                        </Radio.Group>
                                    </FormItem>
                                </Col>
                                )}
                                {logoChanged && (
                                    <Button type="primary" onClick={() => handleUploadNewImage(true)} style={{ marginTop: 16 }} loading={logoLoading}>
                                        {t("upload_new_image")}
                                    </Button>
                                )}
                            </Row>
                        </div>
                    )}
                </div>

                <div>
                    <Divider className={styles.divider} />
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const TrademarkForm = () => {
    return (
        <TrademarkContextProvider>
            <Form />
        </TrademarkContextProvider>
    );
};
export default TrademarkForm;
